import i18n from '@/i18n'
import { filterRulesMapped } from '@/tools/filterRules'
import { workFrontService } from '@/services/work-front.service'
import { enterpriseService } from '@/services/enterprise.service'
import { mapActions, mapGetters } from 'vuex'
import { DialogType, SnackbarStyle } from '@/constants/constants'
import { dialogTypes } from "@/constants/dialogTypes"
import WorkFrontDialogComponent from "@/components/work-front/dialog/WorkFrontDialogComponent.vue"
import { DialogWorkFront, dialogUtil } from "@/tools/dialog"
import { clone } from 'ramda'
import Vue from 'vue'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'WorkFrontABMComponent',
    components: {
        WorkFrontDialogComponent,
        AutocompleteComponent
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos
        tableData: [],
        // key de los datos de la tabla
        tableDataKey: '_id',
        // botones de cada fila
        rowActions: [
            {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen'
            },
            {
                id: 2,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            }
        ],
        // botones del componente Tabla
        tableComponentButtons: [],
        // tamaño de página
        pageSize: undefined,
        // núm de página
        pageNumber: undefined,
        // total de elementos de la base de datos
        pageTotal: 0,
        // dialog de edición
        dialog: {
            ...dialogTypes.WORK_FRONT
        },
        // model del dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('workFront.editDialog.titleNew'),
        // loading de la tabla
        loadingTable: true,
        // arreglo de empresas del selector
        enterprises: [],
        // empresa seleccionada
        enterpriseSelected: null,
        // Opción 'TODOS' para el selector de empresa
        all: {
            _id: 'ALL',
            name: i18n.t('all')
        },
        // para deshabilitar el botón de creación cuando no está seleccionada una empresa
        disableAddButton: false
    }),
    computed: {
        /**
         * getters del breadcrumb
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
        /**
         * Valor computado para hacer las requests al api
         */
        enterpriseSelectedArray() {
            if (this.enterpriseSelected) {
                if (this.enterpriseSelected === this.all._id) {
                    return this.enterprises.filter(enterprise => enterprise._id !== this.all._id)
                        .map(enterprise => enterprise._id)
                } else {
                    return [this.enterpriseSelected]
                }
            } else {
                return []
            }
        }
    },
    created() {
        /*
            Vuex watch del breadcrumb
        */
        this.unwatch = this.$store.watch(
        (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastElement = newBreads[newBreads.length - 1]
                if (!lastElement) {
                    this.cancel()
                    return
                }
                this.dialog = dialogUtil.getDialog(lastElement.key, DialogWorkFront)
            },
        { deep: true })
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
        this.cleanBread()
    },
    mounted() {
        // inicialización de los datos del ABM
        this.initialize()
    },
    methods: {
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'cleanBread'
        ]),
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * Para inicializar el componente
         */
        async initialize () {
            // Botón de eliminación múltiple
            this.tableComponentButtons.push(
                {
                    id: 'edit-dialog',
                    tooltip: i18n.t('add'),
                    icon: 'add',
                    action: this.showCreateDialog.bind(this),
                    selectedData: false
                },
                {
                    id: 'masive-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                })

            // empresas
            await this.initEnterpriseSelector()

            // total de líneas (para el paginador)
            this.pageTotal = await workFrontService.getTotal(this.enterpriseSelectedArray)

            // Si hay query params en la URL los asigna para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Se valida que los query params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = 50
                this.pageNumber = 1
            }

            // cargo la tabla
            this.getData()
            this.resetFields()
            this.initBread()
        },
        /**
         * Inicializa el selector de empresas
         */
        async initEnterpriseSelector() {
            this.enterprises = await enterpriseService.getAllForSelector()
            if (this.enterprises.length > 1) {
                this.enterprises = ([this.all]).concat(this.enterprises)
                this.enterpriseSelected = this.all._id
            } else if (this.enterprises.length === 1) {
                this.enterpriseSelected = this.enterprises[0]._id
            }
        },
        /**
         * Obtiene los datos para la tabla del ABM
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await workFrontService.getTotal(this.enterpriseSelectedArray)
                this.pageSize = 50
                this.pageNumber = 1
            }
            const data = await workFrontService.findAll(this.pageSize, this.pageNumber, !update, this.enterpriseSelectedArray)
            const lastTableHeaders = clone(this.tableHeaders)
            this.tableData.cleanAndUpdate(data.data)
            if (update) {
                const that = this
                Vue.nextTick(function () {
                    that.tableHeaders.cleanAndUpdate(lastTableHeaders)
                })
            } else {
                this.tableHeaders.cleanAndUpdate(data.headers)
                this.setTableFilterRules()
            }
            this.loadingTable = false
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Asigna las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Inicializa el breadcrumb del dialog
         */
        initBread() {
            const bread = {
                key: DialogWorkFront,
                disabled: true,
                text: this.$t('workFront.name')
            }
            this.pushBread(bread)
        },
        /**
         * Para eliminar una línea
         * @param {*} data 
         */
        async delete(data) {
            await workFrontService.deleteOne(data._id)
            this.getData(true)
        },
        /**
         * Para eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('delete'),
                text: this.$t('workFront.multipleDeleteMessage'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    await workFrontService.deleteMany(ids)
                    this.getData(true)
                },
                noAction: () => {
                }
            })
        },
        /**
         * Carga los datos para el modal de edición
         * @param {*} data
         */
        loadFields(data) {
            const modelData = this.dialog.model
            Object.assign(modelData, data)
            modelData.enterprises = clone(this.enterprises.filter(enterprise => enterprise._id !== this.all._id))
            modelData.things = data.things
            modelData.thingIds = data.things.map(thingData => thingData._id)
            this.showUpdateDialog()
        },
        rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    // TO DO: agregar el ID como query param
                    this.loadFields(data)
                    break
                case 2:
                    this.openDialog({
                        title: this.$t('delete'),
                        text: this.$t('workFront.deleteMessage', { name: data.name }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data)
                        },
                        noAction: () => {
                        }
                    })
                    break
                default:
                    console.log('default')
            }
        },
        /**
         * Clic fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Cancelar
         */
        cancel() {
            this.resetFields()
            this.showEditDialog = false
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            this.dialog.model = {
                name: null,
                enterpriseId: null,
                enterprises: [],
                things: [],
                thingIds: [],
                update: false,
                actualName: null
            }
        },
        /**
         * Abre el dialog para creación
         */
        showCreateDialog() {
            this.initBread()
            this.dialog.model.enterprises = clone(this.enterprises.filter(enterprise => enterprise._id !== this.all._id))
            this.dialog.model.enterpriseId =
                this.enterpriseSelected && this.enterpriseSelected !== this.all._id
                    ? this.enterpriseSelected
                    : null
            this.dialog.model.update = false
            this.editDialogTitle= i18n.t('workFront.editDialog.titleNew')
            this.showEditDialog = true
        },
        /**
         * Abre el dialog para actualización
         */
        showUpdateDialog() {
            this.initBread()
            this.dialog.model.update = true
            this.editDialogTitle= i18n.t('workFront.editDialog.titleUpdate')
            this.showEditDialog = true
        },
        /**
         * Paginador externo
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        },
        /**
         * Cada vez que se modifica la selección de empresa, se cargan
         */
        enterpriseSelectedFunction (newValue) {
            this.enterpriseSelected = newValue._id ? newValue._id : newValue;
            this.getData(true)
        },
    },
}
